const isCompletedStep = (step) => Boolean(step?.locked) || [1, '1', '完成'].includes(step?.status);

export const buildVersionWorkflowSteps = (workflow = {}) => {
    const original = workflow.original;
    const inventory = workflow.inventory;
    const consultation = workflow.post_consultation || workflow.consultation;
    const conclusionDone = Boolean(workflow.conclusion_completed) || isCompletedStep(consultation);

    return [
        {
            key: 'original',
            title: '原始版',
            data: original,
            done: Boolean(original),
            locked: true,
            editable: false
        },
        {
            key: 'inventory',
            title: '盤點版',
            data: inventory,
            done: isCompletedStep(inventory),
            locked: isCompletedStep(inventory),
            editable: Boolean(inventory?.id) && !isCompletedStep(inventory)
        },
        {
            key: 'consultation',
            title: '諮詢版',
            data: consultation,
            done: isCompletedStep(consultation),
            locked: isCompletedStep(consultation),
            editable: Boolean(consultation?.id) && !isCompletedStep(consultation)
        },
        {
            key: 'conclusion',
            title: '結論',
            data: null,
            done: conclusionDone,
            locked: conclusionDone,
            editable: isCompletedStep(consultation)
        }
    ];
};

// The API workflow is the source of truth. Entry route and consultation query
// parameters must not hide a chain that already exists.
export const shouldShowVersionWorkflow = (workflow, isExistingSheet = false) =>
    Boolean(isExistingSheet || workflow?.original?.id);

export const buildConsultationVersionEditLocation = (routeName, query, versionId) => ({
    name: String(routeName || '').startsWith('ConsultationWorkspace')
        ? 'ConsultationWorkspaceVersionEdit'
        : 'FinanceVersionEdit',
    params: { id: versionId },
    query: { ...(query || {}) }
});

export const isVersionReadonly = ({ routeName, isEdit, source, status }) => {
    if (
        routeName === 'FinanceVersionView' ||
        routeName === 'ConsultationWorkspaceVersionView'
    ) {
        return true;
    }
    if (!isEdit) return false;

    // 後台版本列表只負責瀏覽歷史／學員版本；流程中的續編必須由諮詢工作台進入。
    if (routeName === 'FinanceVersionEdit') return true;

    const normalizedSource = Number(source);
    const isComplete = status === 1 || status === '1' || status === '完成';
    if (
        routeName === 'ConsultationWorkspaceVersionEdit' ||
        routeName === 'ConsultationWorkspaceVersionCreate'
    ) {
        if (normalizedSource === 0) return true;
        return isComplete;
    }

    return normalizedSource === 0 || isComplete;
};

export const resolveWorkflowSourceAction = (record, workflow = {}) => {
    const sourceLocked =
        isCompletedStep(workflow.inventory) ||
        ['inventory_submitted', 'post_draft', 'consultation_submitted'].includes(workflow.stage);
    if (!record?.is_inventory_source && sourceLocked) {
        return {
            label: '本次諮詢已鎖定',
            mode: 'locked',
            versionId: Number(record?.id)
        };
    }
    if (!record?.is_inventory_source) {
        return { label: '使用此版本諮詢', mode: 'start', versionId: Number(record?.id) };
    }
    const consultation = workflow.post_consultation || workflow.consultation;
    if (consultation?.id) {
        const consultationCompleted =
            Boolean(consultation.locked) ||
            consultation.status === 1 ||
            consultation.status === '1' ||
            consultation.status === '完成';
        return {
            label: consultationCompleted ? '查看結論' : '繼續編輯',
            mode: consultationCompleted ? 'conclusion' : 'edit',
            versionId: Number(consultation.id)
        };
    }
    return {
        label: isCompletedStep(workflow.inventory) ? '檢視盤點' : '繼續編輯',
        mode: isCompletedStep(workflow.inventory) ? 'view' : 'edit',
        versionId: Number(workflow.inventory?.id)
    };
};

export const resolveConsultationWorkspaceVersionId = (workflow, fallbackId = null) => {
    const currentId = Number(workflow?.current_version?.id || 0);
    return currentId > 0 ? currentId : Number(fallbackId) || null;
};

export const buildConsultationConclusionLocation = (userId, versionId, consultationId) => ({
    name: 'ConsultationWorkspaceConclusion',
    params: { userId: Number(userId), version: Number(versionId) },
    query: { consultation_id: Number(consultationId) }
});

export const buildConclusionWorkflow = (conclusion = {}) => {
    const versions = conclusion.versions || {};
    const metrics = conclusion.metrics || {};
    const sheet = (id, values = {}) => id ? { id: Number(id), status: 1, locked: true, ...values } : null;
    return {
        stage: 'consultation_submitted',
        conclusion_completed: true,
        original: sheet(versions.original),
        inventory: sheet(versions.inventory, metrics.inventory),
        post_consultation: sheet(versions.consultation, metrics.consultation)
    };
};

export const canCompleteConsultationWorkflow = (workflow = {}) => {
    const consultation = workflow.post_consultation || workflow.consultation;
    return Boolean(
        workflow.stage === 'consultation_submitted' ||
        consultation?.locked ||
        consultation?.status === 1 ||
        consultation?.status === '1' ||
        consultation?.status === '完成'
    );
};
